import React from 'react';
import cx from 'classnames';
import s from './ProductPrice.scss';
import {ProvidedGlobalProps, withGlobalProps} from '../../providers/globalPropsProvider';
import {LayoutContext} from '../ProductPageApp/ProductPageApp';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {unitsTranslations} from './units-translations';
import {AddToCartState} from '@wix/wixstores-client-storefront-sdk/dist/es/src/services/AddToCartService/constants';
import {PriceBreakdown} from '@wix/wixstores-client-common-components/dist/es/src/PriceBreakdown/PriceBreakdown';
import {BasePrice} from '@wix/wixstores-client-common-components/dist/es/src/BasePrice/BasePrice';
import {ProductType} from '../../constants';
import {getSubscriptionFrequencyTranslationKey} from './subscriptions-translations';
import {clickShippingInfoLinkSf} from '@wix/bi-logger-ec-sf/v2';
import {Dialog} from 'wix-ui-tpa';

export const ProductPriceDataHook = {
  FormattedPrimaryPrice: 'formatted-primary-price',
  SrFormattedPrimaryPrice: 'sr-formatted-primary-price',
  SubscriptionPlanFrequency: 'subscription-plan-frequency',
  FormattedSecondaryPrice: 'formatted-secondary-price',
  SrFormattedSecondaryPrice: 'sr-formatted-secondary-price',
  ProductUnitPrice: 'product-unit-price',
  PriceRangeFrom: 'price-range-from',
  SrPriceRange: 'sr-formatted-price-range',
  PricesWrapper: 'product-prices-wrapper',
  Prices: 'product-price',
  PriceBreakdown: 'price-breakdown',
};

class ProductPriceComponent extends React.Component<ProvidedGlobalProps & IProvidedTranslationProps> {
  private renderPrimary() {
    const {t} = this.props;
    const {formattedActualPrice, formattedPassivePrice, frequency, interval} = this.props.globals.currentPrice;

    const primaryTranslationKey = formattedPassivePrice
      ? 'SR_PRODUCT_PRICE_AFTER_DISCOUNT'
      : 'SR_PRODUCT_PRICE_WHEN_THERE_IS_NO_DISCOUNT';

    return (
      <div className={s.formattedPriceContainer}>
        <span
          data-hook={ProductPriceDataHook.FormattedPrimaryPrice}
          className={s.primary}
          data-wix-price={formattedActualPrice}>
          {formattedActualPrice}
        </span>
        <span className="sr-only" data-hook={ProductPriceDataHook.SrFormattedPrimaryPrice}>
          {t(primaryTranslationKey)}
        </span>
        {frequency && (
          <span data-hook={ProductPriceDataHook.SubscriptionPlanFrequency} className={s.subscriptionPlanFrequency}>
            &nbsp;
            {frequency === 'DAY'
              ? t('PRODUCT_PAGE_SUBSCRIPTION_PRICE_DESCRIPTION', {
                  frequencyUnitSingular: this.getFrequencyUnitSingularTranslation(),
                })
              : t(getSubscriptionFrequencyTranslationKey({frequency, interval}), {
                  numberOfFrequencyUnits: interval,
                })}
          </span>
        )}
      </div>
    );
  }

  private renderSecondary() {
    const {t} = this.props;
    const {formattedPassivePrice} = this.props.globals.currentPrice;

    return (
      <div className={s.formattedPriceContainer}>
        <span
          data-hook={ProductPriceDataHook.FormattedSecondaryPrice}
          className={s.secondary}
          data-wix-original-price={formattedPassivePrice}>
          {` ${formattedPassivePrice} `}
        </span>
        <span className="sr-only" data-hook={ProductPriceDataHook.SrFormattedSecondaryPrice}>
          {t('SR_PRODUCT_PRICE_BEFORE_DISCOUNT')}
        </span>
      </div>
    );
  }

  private getFrequencyUnitSingularTranslation(): string {
    const {t} = this.props;
    const {frequency} = this.props.globals.currentPrice;

    /* istanbul ignore next: code unreachable, should be removed when UseNewExpressCheckoutButtonsInProductPage FT is merged */
    switch (frequency) {
      case 'DAY':
        return 'DAY_QA';
      case 'WEEK':
        return t('PRODUCT_PAGE_PLAN_WEEK');
      case 'MONTH':
        return t('PRODUCT_PAGE_PLAN_MONTH');
      case 'YEAR':
        return t('PRODUCT_PAGE_PLAN_YEAR');
    }
  }

  private getUnitTranslations(quantity: number, isAbbreviation: boolean = false): string {
    const {t} = this.props;
    const {baseMeasurementUnit} = this.props.globals.currentPrice;

    if (isAbbreviation) {
      return t(unitsTranslations[baseMeasurementUnit].abbr);
    } else {
      return quantity === 1
        ? t(unitsTranslations[baseMeasurementUnit].singular)
        : t(unitsTranslations[baseMeasurementUnit].plural);
    }
  }

  private renderPrice() {
    const {formattedPassivePrice} = this.props.globals.currentPrice;

    return (
      <>
        {Boolean(formattedPassivePrice) && this.renderSecondary()}
        {this.renderPrimary()}
      </>
    );
  }

  private renderPriceRange(formattedFromPrice: string) {
    const {t} = this.props;
    return (
      <>
        <span className="sr-only" data-hook={ProductPriceDataHook.SrPriceRange}>
          {t('SR_PRODUCT_PRICE_WHEN_THERE_IS_NO_DISCOUNT')}
        </span>
        <span data-hook={ProductPriceDataHook.PriceRangeFrom}>
          {t('productPage.price.from.label', {formattedAmount: formattedFromPrice})}
        </span>
      </>
    );
  }

  private readonly sendClickShippingInfoLinkSfEvent = () => {
    const {reportBI, product} = this.props.globals;
    reportBI(
      clickShippingInfoLinkSf({
        productId: product.id,
      })
    );
  };

  public render(): JSX.Element {
    const {addToCartState, product} = this.props.globals;
    const {shouldRenderTaxDisclaimer, taxDisclaimer, shippingDisclaimer} = this.props.globals.priceBreakdown;
    const {isZeroPrice, formattedActualPrice, formattedPricePerUnit, formattedFromPrice} =
      this.props.globals.currentPrice;

    const isAddToCartDisabled = addToCartState === AddToCartState.DISABLED;
    const shouldRenderUnitPrice = !!formattedPricePerUnit;
    const hasFormattedPrice = !!formattedActualPrice;

    if ((isAddToCartDisabled && isZeroPrice) || !hasFormattedPrice) {
      return null;
    }

    return (
      <LayoutContext.Consumer>
        {(layoutName) => (
          <div data-hook={ProductPriceDataHook.PricesWrapper} className={s.pricesWrapper}>
            <div data-hook={ProductPriceDataHook.Prices} className={cx(s.prices, s[`prices__${layoutName}`])}>
              {formattedFromPrice ? this.renderPriceRange(formattedFromPrice) : this.renderPrice()}
            </div>
            {shouldRenderUnitPrice && this.renderUnitPrice()}
            {(shouldRenderTaxDisclaimer || shippingDisclaimer?.show) && (
              <PriceBreakdown
                shouldRenderTaxDisclaimer={shouldRenderTaxDisclaimer}
                taxDisclaimerLabel={taxDisclaimer}
                shippingDisclaimer={shippingDisclaimer}
                whenShippingDisclaimerDialogOpen={this.sendClickShippingInfoLinkSfEvent}
                isRTL={this.props.globals.isRTL}
                isDigitalProduct={product.productType === ProductType.DIGITAL}
                className={s.priceBreakdown}
                DialogComponent={Dialog}
              />
            )}
          </div>
        )}
      </LayoutContext.Consumer>
    );
  }

  private renderUnitPrice() {
    const {t} = this.props;
    const {formattedPricePerUnit, baseQuantity} = this.props.globals.currentPrice;
    const unitTranslation = this.getUnitTranslations(baseQuantity);
    const srFormattedUnitAndQuantity = t('productPage.gallery.pricePerUnit.ariaLabel', {
      price: formattedPricePerUnit,
      quantity: baseQuantity,
      unit: unitTranslation,
    });

    return (
      <BasePrice
        data-hook={ProductPriceDataHook.ProductUnitPrice}
        className={s.unitPrice}
        formattedPricePerUnit={formattedPricePerUnit}
        baseQuantity={baseQuantity}
        unitTranslation={this.getUnitTranslations(0, true)}
        screenReaderText={srFormattedUnitAndQuantity}
        withRTLSupport
      />
    );
  }
}

export const ProductPrice: React.FC<ProvidedGlobalProps & IProvidedTranslationProps> = withGlobalProps(
  withTranslations('globals.texts')(ProductPriceComponent)
);
